import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogoInline } from '../LogoInline.tsx';
import { PillButton } from '../PillButton.tsx';
import { EYEBROW } from './cmsTheme.ts';

interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly to?: string;
  readonly anchor?: boolean;
}

interface BurgerItem {
  readonly label: string;
  readonly to: string;
}

export interface TopNavProps {
  onJump?: (id: string) => void;
  mode?: 'page' | 'subpage';
}

export function TopNav({ onJump, mode = 'page' }: TopNavProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Desktop nav - primære destinationer
  const items: readonly NavItem[] = [
    { id: 'program',   label: 'Program',   to: '/program'  },
    { id: 'camping',   label: 'Camping',   to: '/camping'  },
    { id: 'praktisk',  label: 'Praktisk',  to: '/praktisk' },
    { id: 'nyheder',   label: 'Nyheder',   to: '/nyheder'  },
  ];

  // Burger-menu - primære + vigtigste sekundære sider
  const burgerPrimary: readonly BurgerItem[] = [
    { label: 'Program',  to: '/program'  },
    { label: 'Torsdag',  to: '/torsdag'  },
    { label: 'Camping',  to: '/camping'  },
    { label: 'Praktisk', to: '/praktisk' },
    { label: 'Nyheder',  to: '/nyheder'  },
  ];

  const burgerSecondary: readonly BurgerItem[] = [
    { label: 'Frivillig',          to: '/frivillig'        },
    { label: 'Sponsorer & fonde',  to: '/partnere'         },
    { label: 'Arrangørgruppen',    to: '/arrangoergruppen' },
    { label: 'Sekretariatet',      to: '/sekretariatet'    },
    { label: 'Foreningen',         to: '/foreningen'       },
    { label: 'Om Midtfyns',            to: '/om-midtfyns'      },
    { label: 'Kontakt',                to: '/kontakt'          },
  ];

  const handleAnchor = (id: string): void => {
    if (mode === 'page') {
      onJump?.(id);
    } else if (id === 'tidsplan') {
      navigate('/program#tidsplan');
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-mf-yellow/90 backdrop-blur border-b border-mf-green/20">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-4 text-mf-black">
          <Link to="/" className="flex items-center text-mf-black">
            <LogoInline className="h-5 md:h-6" />
          </Link>
          <span className={`hidden md:block ${EYEBROW} text-mf-black border-l border-mf-green/20 pl-4`}>
            Første weekend i september
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-8 text-mf-black text-[15px]">
          {items.map(it => it.anchor ? (
            <button
              key={it.id}
              onClick={() => handleAnchor(it.id)}
              className="hover:text-mf-coral transition-colors font-medium"
            >
              {it.label}
            </button>
          ) : (
            <Link key={it.id} to={it.to!} className="hover:text-mf-coral transition-colors font-medium">
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <PillButton
            variant="coral"
            onClick={() => navigate('/billetter')}
            className="hidden sm:inline-flex"
          >
            <span aria-hidden>🎟</span> Køb billet
          </PillButton>
          <button
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-mf-green/20 text-mf-black"
            onClick={() => setOpen(o => !o)}
            aria-label="Menu"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
              {open
                ? <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                : <>
                    <path d="M4 7h16" strokeLinecap="round" />
                    <path d="M4 12h16" strokeLinecap="round" />
                    <path d="M4 17h16" strokeLinecap="round" />
                  </>}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-mf-green/20 bg-mf-yellow">
          <div className="max-w-[1200px] mx-auto px-5 py-4">
            <ul className="flex flex-col gap-0.5">
              {burgerPrimary.map(it => (
                <li key={it.to}>
                  <Link
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className="block py-2.5 text-mf-black text-lg font-medium hover:text-mf-coral transition-colors"
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="my-3 border-t border-mf-green/20" />

            <ul className="flex flex-col gap-0.5">
              {burgerSecondary.map(it => (
                <li key={it.to}>
                  <Link
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className="block py-2 text-mf-black/75 text-base hover:text-mf-coral transition-colors"
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-4 pt-3 border-t border-mf-green/20">
              <button
                onClick={() => { setOpen(false); navigate('/billetter'); }}
                className="w-full bg-mf-coral text-white rounded-full py-3 font-medium text-base hover:bg-mf-coralImg transition-colors"
              >
                🎟 Køb billet
              </button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
